import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ActivityLog, PaginationInfo } from "../../types/models";
import { ActivityLogContextType, ActivityLogFilters } from "../../types/context";

export const ActivityLogContext = createContext<ActivityLogContextType | undefined>(undefined);

interface ActivityLogProviderProps {
  children: ReactNode;
}

export const ActivityLogProvider: React.FC<ActivityLogProviderProps> = ({ children }) => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivityLogs = useCallback(async (filters: ActivityLogFilters = {}, page: number = 1, perPage: number = 15): Promise<{ data: ActivityLog[]; pagination: PaginationInfo | null }> => {
    setLoading(true);
    setError(null);
    try {
      let url = '/activity-logs';
      const params = new URLSearchParams();
      if (filters.model_type) params.append('model_type', filters.model_type);
      if (filters.model_id) params.append('model_id', filters.model_id.toString());
      if (filters.action) params.append('action', filters.action);
      if (filters.user_id) params.append('user_id', filters.user_id.toString());
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      const logs = response.data.data?.data || response.data.data || [];
      setActivityLogs(logs);
      
      // Extract pagination info from response
      const paginationInfo: PaginationInfo | null = response.data.data?.pagination || 
                            response.data.pagination || 
                            (response.data.data?.current_page ? {
                              current_page: response.data.data.current_page,
                              last_page: response.data.data.last_page,
                              per_page: response.data.data.per_page,
                              total: response.data.data.total,
                            } : null);
      
      return {
        data: logs,
        pagination: paginationInfo,
      };
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      console.error("Failed to fetch activity logs:", err);
      return { data: [], pagination: null };
    } finally {
      setLoading(false);
    }
  }, []);

  const getActivityLog = useCallback(async (id: number): Promise<ActivityLog> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/activity-logs/${id}`);
      return response.data.data;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value: ActivityLogContextType = {
    activityLogs,
    loading,
    error,
    fetchActivityLogs,
    getActivityLog,
  };

  return (
    <ActivityLogContext.Provider value={value}>
      {children}
    </ActivityLogContext.Provider>
  );
};

export default ActivityLogProvider;

